import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { entities } from "../services/api";
import {
  HomeIcon,
  ProductIcon,
  ContainerIcon,
  LocationIcon,
  OrganizeIcon,
  SettingsIcon,
  UserIcon,
  SearchIcon,
  PlusIcon,
  TrashIcon,
  QRCodeIcon,
  CameraIcon,
  GridIcon,
  DocumentIcon,
  EmailIcon,
  ShoppingBagIcon,
  ClipboardListIcon,
  TrendingUpIcon,
} from "./Icons";

const commands = [
  // Actions
  {
    id: "quick-add",
    name: "Quick Add from Receipt",
    action: "quickAdd",
    icon: DocumentIcon,
    keywords: ["quick", "add", "receipt", "paste", "amazon", "import"],
  },
  {
    id: "create",
    name: "Create New Item",
    path: "/create",
    icon: PlusIcon,
    keywords: ["create", "new", "add", "item"],
  },
  {
    id: "scan",
    name: "Scan QR Code",
    path: "/scan",
    icon: CameraIcon,
    keywords: ["scan", "camera", "qr"],
  },
  // Navigation
  {
    id: "home",
    name: "Go to Dashboard",
    path: "/",
    icon: HomeIcon,
    keywords: ["home", "dashboard", "main"],
  },
  {
    id: "products",
    name: "Go to Products",
    path: "/products",
    icon: ProductIcon,
    keywords: ["products", "items", "inventory"],
  },
  {
    id: "containers",
    name: "Go to Containers",
    path: "/containers",
    icon: ContainerIcon,
    keywords: ["containers", "boxes", "storage"],
  },
  {
    id: "locations",
    name: "Go to Locations",
    path: "/locations",
    icon: LocationIcon,
    keywords: ["locations", "places", "where"],
  },
  {
    id: "organize",
    name: "Go to Organize",
    path: "/organize",
    icon: OrganizeIcon,
    keywords: ["organize", "move", "arrange"],
  },
  {
    id: "views",
    name: "Go to Custom Views",
    path: "/views",
    icon: GridIcon,
    keywords: ["views", "custom", "grid", "layout"],
  },
  {
    id: "qr-codes",
    name: "QR Code Manager",
    path: "/qr-codes",
    icon: QRCodeIcon,
    keywords: ["qr", "codes", "print", "generate"],
  },
  {
    id: "trash",
    name: "Go to Trash",
    path: "/trash",
    icon: TrashIcon,
    keywords: ["trash", "deleted", "remove"],
  },
  // Sales
  {
    id: "items-sold",
    name: "Go to Items Sold",
    path: "/sales/sold",
    icon: ShoppingBagIcon,
    keywords: ["sold", "sales", "orders", "profit"],
  },
  {
    id: "items-listed",
    name: "Go to Items Listed",
    path: "/sales/listed",
    icon: ClipboardListIcon,
    keywords: ["listed", "listings", "sales", "ebay", "offerup"],
  },
  {
    id: "sales-analytics",
    name: "Go to Analytics",
    path: "/sales/analytics",
    icon: TrendingUpIcon,
    keywords: ["analytics", "sales", "stats", "profit", "charts"],
  },
  {
    id: "email-scanning",
    name: "Go to Email Scanning",
    path: "/email-scanning",
    icon: EmailIcon,
    keywords: ["email", "scanning", "receipts", "gmail", "import"],
  },
  {
    id: "settings",
    name: "Go to Settings",
    path: "/settings",
    icon: SettingsIcon,
    keywords: ["settings", "preferences", "config"],
  },
  {
    id: "account",
    name: "Go to Account",
    path: "/account",
    icon: UserIcon,
    keywords: ["account", "profile", "user"],
  },
];

const entityTypeIcons = {
  product: ProductIcon,
  container: ContainerIcon,
  location: LocationIcon,
};

function CommandPalette({ isOpen, onClose, onQuickAdd }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filteredCommands = commands.filter((cmd) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      cmd.name.toLowerCase().includes(searchLower) ||
      cmd.keywords.some((k) => k.includes(searchLower))
    );
  });

  // Inventory search: query the entities API (debounced) when the input
  // doesn't just match commands
  useEffect(() => {
    const q = search.trim();
    if (!isOpen || q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await entities.search(q);
        if (!cancelled) setResults(data);
      } catch (err) {
        console.error("Inventory search failed:", err);
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, isOpen]);

  // Commands first, then matching inventory entities
  const items = [
    ...filteredCommands.map((cmd) => ({ kind: "command", id: cmd.id, cmd })),
    ...results.map((entity) => ({
      kind: "entity",
      id: `entity-${entity.id}`,
      entity,
    })),
  ];

  const executeItem = useCallback(
    (item) => {
      if (item.kind === "entity") {
        navigate(`/entity/${item.entity.id}`);
      } else if (item.cmd.action === "quickAdd") {
        onQuickAdd?.();
      } else if (item.cmd.path) {
        navigate(item.cmd.path);
      }
      onClose();
    },
    [navigate, onClose, onQuickAdd],
  );

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset state whenever the palette closes (Escape, overlay click, command)
  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setSelectedIndex(0);
      setResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  useEffect(() => {
    const selectedEl = listRef.current?.querySelector(
      ".command-palette-item-selected",
    );
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex, filteredCommands.length, results.length]);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (items[selectedIndex]) {
          executeItem(items[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="command-palette-overlay" onClick={onClose}>
      <div
        className="command-palette"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="command-palette-input-wrapper">
          <SearchIcon size={18} className="command-palette-search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="command-palette-input"
            placeholder="Type a command or search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <kbd className="command-palette-kbd">esc</kbd>
        </div>

        <div className="command-palette-list" ref={listRef}>
          {items.length === 0 ? (
            <div className="command-palette-empty">
              {searching
                ? "Searching inventory..."
                : search.trim().length >= 2
                  ? "No results found"
                  : "No commands found"}
            </div>
          ) : (
            items.map((item, index) => {
              const isEntity = item.kind === "entity";
              const Icon = isEntity
                ? entityTypeIcons[item.entity.entity_type] || ProductIcon
                : item.cmd.icon;
              const name = isEntity ? item.entity.name : item.cmd.name;
              return (
                <button
                  key={item.id}
                  className={`command-palette-item ${index === selectedIndex ? "command-palette-item-selected" : ""}`}
                  onClick={() => executeItem(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <Icon size={18} className="command-palette-item-icon" />
                  <span className="command-palette-item-name">{name}</span>
                  {isEntity && index !== selectedIndex && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "0.7rem",
                        color: "var(--text-muted)",
                        textTransform: "capitalize",
                        flexShrink: 0,
                      }}
                    >
                      {item.entity.entity_type}
                    </span>
                  )}
                  {index === selectedIndex && (
                    <kbd className="command-palette-kbd command-palette-kbd-enter">
                      enter
                    </kbd>
                  )}
                </button>
              );
            })
          )}
        </div>

        <div className="command-palette-footer">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> to navigate
          </span>
          <span>
            <kbd>enter</kbd> to select
          </span>
          <span>
            <kbd>esc</kbd> to close
          </span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
